import React, { useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { subYears } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { useModal } from 'mui-modal-provider';
import { yup } from '../../../../../services/yup';
import { useBind } from '../../../../../hooks/use-bind';
import { CREATE_DEPENDENT } from '../../../../../graphql/profile/mutations';
import { useActionsInProgress } from '../../../../../graphql/preloader/actions/actions-in-progress';
import { useCancellablePromise } from '../../../../../hooks/use-cancellable-promise';
import { ProfileForm } from './profile-form';
import { KidsProfileFormData } from './profile-form/profile-form.models';
import { Page } from '../../page/page';
import { InternalLinks, SECONDARY_BACKGROUND } from '../../../../../constants';
import { ContentWrapper } from '../../../../common/content-wrapper';
import { Box } from '../../../../common/box';
import { ProfileLink } from '../profile-link';
import qIcon from '../../../../../assets/images/kids/question.svg';
import { useStyles } from './create-kids-profile.styles';
import { toDate } from '../../../../../utils/date/to-date';
import {
  useFirstNameCyrillicValidation,
} from '../../../../../hooks/use-first-name-cyrillic-validation';
import {
  useLastNameCyrillicValidation,
} from '../../../../../hooks/use-last-name-cyrillic-validation';
import { convertDateToString } from '../../../../../utils/date/convert-date-to-string';
import { ErrorModal } from '../error-modal';
import { GET_DEPENDENTS_COUNTER } from '../../../../../graphql/profile/queries';
import { DEPENDENTS_LIMIT } from '../../../../../constants/profile';
import { v2025PP16783 } from '../../../../../constants/veeva';
import { isDependentCreated } from '../../../../../graphql/user/user.cache';

export const CreateKidsProfile = (): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const { data: profileData, error: profileError } = useQuery<{ profile: { dependentsCounter: number } }>(GET_DEPENDENTS_COUNTER);
  const isOverlimit = !!profileData?.profile?.dependentsCounter
    && (profileData?.profile?.dependentsCounter === DEPENDENTS_LIMIT
    || profileData?.profile?.dependentsCounter > DEPENDENTS_LIMIT );

  const [createDependent, createDependentMutationSettings] = useMutation(CREATE_DEPENDENT);
  const { loading } = createDependentMutationSettings;
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const firstNameValidation = useFirstNameCyrillicValidation();
  const lastNameValidation = useLastNameCyrillicValidation();
  const maxDateOfBirth = useMemo(() => new Date(), []);
  const minDate = useMemo(() => toDate(subYears(new Date(), 18)), []);

  const validationSchema = useMemo(
    () => yup
      .object({
        firstName: firstNameValidation,
        lastName: lastNameValidation,
        clExperience: yup.string().required(intl.formatMessage({ id: 'common.errors.required.long' })),
        consent: yup
          .bool()
          .required(intl.formatMessage({ id: 'common.errors.required.long' })),
        dateOfBirth: yup
          .date()
          .nullable()
          .required(intl.formatMessage({ id: 'common.errors.required.long' }))
          .max(
            maxDateOfBirth,
            intl.formatMessage({ id: 'common.errors.required.long' }),
          )
          .min(minDate),
      }),
    [firstNameValidation, lastNameValidation, maxDateOfBirth, minDate, intl],
  );

  const loadingBind = useBind(loading);
  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const createDependentBind = useBind(createDependent);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);
  const { showModal } = useModal();
  const navigate = useNavigate();

  const handleError = () => {
    showModal(ErrorModal, {
      title: 'createKidsProfile.error.title',
      text: 'createKidsProfile.error.text',
      firstButton: 'createKidsProfile.error.button',
    });
  };

  const handleCreateDependent = useCallback(async (data: KidsProfileFormData) => {
    if (loadingBind.current || !data) {
      return;
    }

    if (!!profileError || isOverlimit) {
      showModal(ErrorModal, {
        title: 'addKidsProfile.error.title',
        text: 'addKidsProfile.error.text',
        firstButton: 'common.errors.callSupport',
        secondButton: 'common.gotIt',
        firstButtonUrl: intl.formatMessage({ id: 'common.phoneNumber.support.href' }),
      });

      return;
    }

    addActionInProgressBind.current();

    try {
      removeActionInProgressBind.current();
      addActionInProgressBind.current();

      try {
        await makeCancellablePromiseBind.current(
          createDependentBind.current({
            variables: {
              dependent: {
                firstName: data.firstName?.trim(),
                lastName: data.lastName?.trim(),
                dateOfBirth: convertDateToString(data.dateOfBirth),
                clExperience: data.clExperience,
                registrationSource: 'PWA',
                consent: {
                  consent: 'tnc',
                  value: data.consent,
                  date: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
                },
              },
            },
          }),
        );

        isDependentCreated(true);
        removeActionInProgressBind.current();
        navigate(InternalLinks.forKids);
      } catch (error) {
        removeActionInProgressBind.current();
        handleError();

        if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
          return;
        }
      }
    } catch (error: any) {
      removeActionInProgressBind.current();

      handleError();
    }
  }, [
    loadingBind,
    makeCancellablePromiseBind,
    CancelledPromiseOnUnmountErrorBind,
    createDependent,
    addActionInProgressBind,
    removeActionInProgressBind,
    isOverlimit,
    profileError,
  ]);

  return (
    <Page
      title={intl.formatMessage({ id: 'createKidsProfile.pageTitle' })}
      style={`body {background-color: ${SECONDARY_BACKGROUND};}`}
    >
      <ContentWrapper withoutPaddings>
        <Box title={<FormattedMessage id="createKidsProfile.title" />} isBackButton>
          <div className={classes.veevaNumber}>{v2025PP16783}</div>
          <div className={classes.link}>
            <ProfileLink text="profileAbout.title" url={InternalLinks.aboutProfile} image={qIcon} />
          </div>
          <ProfileForm
            onSubmit={handleCreateDependent}
            validationSchema={validationSchema}
          />
        </Box>
      </ContentWrapper>
    </Page>
  );
};
